import axios from "axios";
import { DEFAULT_STAGES } from "@/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Attach the global teacher token (if signed in) to any /api/classes or /api/teacher/classes call,
// falling back to the active pupil's scoped token so pupils can move their own rocket / submit tests.
axios.interceptors.request.use((config) => {
  try {
    const u = String(config.url || "");
    if (u.includes("/api/classes") || u.includes("/api/teacher/classes")) {
      const t = localStorage.getItem("teacher_token");
      if (t) config.headers = { ...(config.headers || {}), Authorization: `Bearer ${t}` };
      else {
        const pt = localStorage.getItem("pupil_token");
        if (pt) config.headers = { ...(config.headers || {}), Authorization: `Bearer ${pt}` };
      }
    }
  } catch {}
  return config;
});

// ---- Pupil scoped tokens (per pupil id, so a shared device can switch pupils) ----
const PUPIL_TOKENS_KEY = "starlight_pupil_tokens_v1";
function readPupilTokens() {
  try { return JSON.parse(localStorage.getItem(PUPIL_TOKENS_KEY)) || {}; } catch { return {}; }
}
function storePupilToken(pupilId, token) {
  if (!pupilId || !token) return;
  const m = readPupilTokens();
  m[pupilId] = token;
  try { localStorage.setItem(PUPIL_TOKENS_KEY, JSON.stringify(m)); } catch {}
  localStorage.setItem("pupil_token", token);
}
export function setActivePupilToken(pupilId) {
  const m = readPupilTokens();
  if (m[pupilId]) localStorage.setItem("pupil_token", m[pupilId]);
}
export function clearActivePupilToken() {
  localStorage.removeItem("pupil_token");
}
const LS_KEY = "starlight_classes_cache_v1";
const QUEUE_KEY = "starlight_pending_ops_v1";

// ---- Local cache (offline resilience) ----
export function readCache() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY)) || [];
  } catch {
    return [];
  }
}

export function writeCache(classes) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(classes));
  } catch {}
}

function upsertCache(room) {
  const list = readCache();
  const idx = list.findIndex((c) => c.id === room.id);
  if (idx >= 0) list[idx] = room;
  else list.push(room);
  writeCache(list);
}

function removeCache(id) {
  writeCache(readCache().filter((c) => c.id !== id));
}

function getLocal(id) {
  return readCache().find((c) => c.id === id) || null;
}

// ---- Offline write queue ----
function readQueue() {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY)) || [];
  } catch {
    return [];
  }
}

function writeQueue(q) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  } catch {}
}

export function getQueueLength() {
  return readQueue().length;
}

function pendingClassIds() {
  const ids = new Set();
  for (const op of readQueue()) ids.add(op.id || op.classId);
  return ids;
}

function enqueue(op) {
  const q = readQueue();
  if (op.type === "deleteClass") {
    // A delete supersedes any pending upsert/progress for that class.
    const filtered = q.filter((o) => (o.id || o.classId) !== op.id);
    filtered.push(op);
    writeQueue(filtered);
    return;
  }
  if (op.type === "upsertClass") {
    // Collapse to the latest full snapshot for that class.
    const filtered = q.filter((o) => !(o.type === "upsertClass" && o.id === op.id));
    filtered.push(op);
    writeQueue(filtered);
    return;
  }
  if (op.type === "progress") {
    // If a full upsert is already queued for this class it carries the latest
    // pupil state, so a separate progress op is redundant.
    const hasUpsert = q.some((o) => o.type === "upsertClass" && o.id === op.classId);
    if (hasUpsert) return;
    const filtered = q.filter(
      (o) => !(o.type === "progress" && o.classId === op.classId && o.pupilId === op.pupilId)
    );
    filtered.push(op);
    writeQueue(filtered);
    return;
  }
  q.push(op);
  writeQueue(q);
}

// ---- Progress history helpers ----
export function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

// Sets a pupil's stage and records the date it first reached that stage.
export function applyProgress(pupil, stageIndex, date) {
  const d = date || todayStr();
  let history = pupil.history || [];
  if (stageIndex > 0 && !history.some((h) => h.stageIndex === stageIndex)) {
    history = [...history, { stageIndex, date: d }];
  }
  return { ...pupil, stageIndex, history };
}

async function sendOp(op) {
  if (op.type === "upsertClass") {
    await axios.post(`${API}/classes`, op.body);
  } else if (op.type === "deleteClass") {
    await axios.delete(`${API}/classes/${op.id}`);
  } else if (op.type === "progress") {
    await axios.patch(`${API}/classes/${op.classId}/pupils/${op.pupilId}/progress`, {
      stageIndex: op.stageIndex,
      date: op.date,
    });
  }
}

let flushing = false;
export async function flushQueue() {
  if (flushing) return getQueueLength();
  flushing = true;
  try {
    const q = readQueue();
    const remaining = [];
    for (const op of q) {
      try {
        await sendOp(op);
      } catch {
        remaining.push(op); // still offline / server error — keep for later
      }
    }
    writeQueue(remaining);
    const done = q.length - remaining.length;
    if (done > 0) {
      window.dispatchEvent(
        new CustomEvent("starlight-sync", { detail: { synced: done, remaining: remaining.length } })
      );
    }
    return remaining.length;
  } finally {
    flushing = false;
  }
}

export function initSync() {
  window.addEventListener("online", () => flushQueue());
  // Attempt a flush shortly after startup (in case we came back online).
  setTimeout(() => flushQueue(), 800);
}

// ---- API with offline fallback + queue ----
export async function fetchClasses() {
  try {
    const { data } = await axios.get(`${API}/teacher/classes`);
    writeCache(data);
    return { data, offline: false };
  } catch (e) {
    if (e?.response?.status === 401) return { data: [], authRequired: true };
    return { data: readCache(), offline: true };
  }
}

export async function teacherRegister(name) {
  const { data } = await axios.post(`${API}/teacher/register`, { name });
  if (data.token) localStorage.setItem("teacher_token", data.token);
  return data; // { id, name, code, token }
}

export async function teacherLogin(code) {
  const { data } = await axios.post(`${API}/teacher/login`, { code });
  if (data.token) localStorage.setItem("teacher_token", data.token);
  return data; // { id, name, code, token }
}

export function teacherLogout() {
  localStorage.removeItem("teacher_token");
}

export async function pupilLogin(code) {
  const { data } = await axios.post(`${API}/pupil/login`, { code });
  storePupilToken(data.pupilId, data.token);
  return data; // { classCode, classId, pupilId, token, class }
}

export async function fetchClass(id) {
  try {
    const { data } = await axios.get(`${API}/classes/${id}`);
    if (pendingClassIds().has(id)) {
      return { data: getLocal(id) || data, offline: false };
    }
    upsertCache(data);
    return { data, offline: false };
  } catch (e) {
    if (e?.response?.status === 401) return { data: null, authRequired: true };
    return { data: getLocal(id), offline: true };
  }
}

export async function joinByCode(code) {
  const { data } = await axios.get(`${API}/join/${encodeURIComponent(code.trim().toUpperCase())}`);
  upsertCache(data);
  return data;
}

export async function addPupil(classId, { name, color, variant }) {
  const { data } = await axios.post(`${API}/classes/${classId}/pupils`, { name, color, variant });
  storePupilToken(data.id, data.token);
  try {
    const c = await axios.get(`${API}/classes/${classId}`);
    upsertCache(c.data);
  } catch {}
  return data;
}

export async function updatePupil(classId, pupilId, patch) {
  const { data } = await axios.put(`${API}/classes/${classId}/pupils/${pupilId}`, patch);
  try {
    const c = await axios.get(`${API}/classes/${classId}`);
    upsertCache(c.data);
  } catch {}
  return data;
}

export async function createClass(name, stages = DEFAULT_STAGES) {
  const body = { name, stages, pupils: [] };
  const { data } = await axios.post(`${API}/classes`, body);
  upsertCache(data);
  return data;
}

export async function createTeacherInvite(classId) {  const { data } = await axios.post(`${API}/classes/${classId}/teacher/invite`);
  return data; // { token }
}

export async function getTeacherInvite(token) {
  const { data } = await axios.get(`${API}/teacher-invite/${token}`);
  return data; // { classId, className, used, expired }
}

export async function acceptTeacherInvite(token, name) {
  const { data } = await axios.post(`${API}/teacher-invite/${token}/accept`, { name });
  if (data.token) localStorage.setItem("teacher_token", data.token);
  return data; // { classId, className, teacherName, teacherCode, pupilId, token }
}

export async function removeTeacher(classId, teacherId) {
  const { data } = await axios.delete(`${API}/classes/${classId}/teachers/${teacherId}`);
  try {
    const c = await axios.get(`${API}/classes/${classId}`);
    upsertCache(c.data);
  } catch {}
  return data; // { ok }
}

export async function addProject(classId, name, stages) {
  const body = stages ? { name, stages } : { name };
  const { data } = await axios.post(`${API}/classes/${classId}/projects`, body);
  upsertCache(data);
  return data;
}

export async function updateProject(classId, projectId, patch) {
  const { data } = await axios.put(`${API}/classes/${classId}/projects/${projectId}`, patch);
  upsertCache(data);
  return data;
}

export async function deleteProject(classId, projectId) {
  const { data } = await axios.delete(`${API}/classes/${classId}/projects/${projectId}`);
  upsertCache(data);
  return data;
}

export async function setActiveProject(classId, projectId) {
  const { data } = await axios.put(`${API}/classes/${classId}/active-project`, { projectId });
  upsertCache(data);
  return data;
}

export async function submitAssessment(classId, projectId, pupilId, answers) {
  const { data } = await axios.post(
    `${API}/classes/${classId}/projects/${projectId}/assessment/submit`,
    { pupilId, answers }
  );
  return data; // { score, total, percent, passed, date }
}

export async function setRubric(classId, pupilId, projectId, marks) {
  const { data } = await axios.put(`${API}/classes/${classId}/pupils/${pupilId}/rubric`, { projectId, marks });
  try {
    const c = await axios.get(`${API}/classes/${classId}`);
    upsertCache(c.data);
  } catch {}
  return data;
}

export async function setComment(classId, pupilId, projectId, comment) {
  const { data } = await axios.put(`${API}/classes/${classId}/pupils/${pupilId}/comment`, { projectId, comment });
  try {
    const c = await axios.get(`${API}/classes/${classId}`);
    upsertCache(c.data);
  } catch {}
  return data;
}

export async function setWhiteboards(classId, whiteboards) {
  const { data } = await axios.put(`${API}/classes/${classId}/whiteboards`, { whiteboards });
  upsertCache(data);
  return data;
}

export async function updateClass(id, payload) {
  const cached = getLocal(id);
  const merged = { ...cached, ...payload, updatedAt: new Date().toISOString() };
  upsertCache(merged);
  const body = { id, name: merged.name, stages: merged.stages, pupils: merged.pupils };
  try {
    const { data } = await axios.put(`${API}/classes/${id}`, {
      name: merged.name,
      stages: merged.stages,
      pupils: merged.pupils,
    });
    upsertCache(data);
    return { data, offline: false };
  } catch {
    enqueue({ type: "upsertClass", id, body });
    return { data: merged, offline: true };
  }
}

export async function setAutoDelete(classId, autoDeleteAt) {
  const { data } = await axios.put(`${API}/classes/${classId}`, { autoDeleteAt: autoDeleteAt || "" });
  upsertCache(data);
  return data;
}

export async function submitDataRequest(payload) {
  const { data } = await axios.post(`${API}/data-requests`, payload);
  return data; // { ok, id }
}

export async function deleteClass(id) {
  try {
    await axios.delete(`${API}/classes/${id}`);
    removeCache(id);
    return { offline: false };
  } catch (e) {
    const status = e?.response?.status;
    if (status === 401 || status === 403) {
      // Needs teacher authorization — surface to the UI (401 = signed out, 403 = not your class).
      const err = new Error("teacher authorization required");
      err.authRequired = true;
      throw err;
    }
    // Network/offline error — queue the delete for later.
    removeCache(id);
    enqueue({ type: "deleteClass", id });
    return { offline: true };
  }
}

export async function updateProgress(classId, pupilId, stageIndex) {
  const date = todayStr();
  const cached = getLocal(classId);
  if (cached) {
    cached.pupils = cached.pupils.map((p) =>
      p.id === pupilId ? applyProgress(p, stageIndex, date) : p
    );
    upsertCache(cached);
  }
  try {
    const { data } = await axios.patch(
      `${API}/classes/${classId}/pupils/${pupilId}/progress`,
      { stageIndex, date }
    );
    upsertCache(data);
    return { data, offline: false };
  } catch {
    enqueue({ type: "progress", classId, pupilId, stageIndex, date });
    return { data: getLocal(classId), offline: true };
  }
}

// ---- Desktop backup ----
export function exportBackup() {
  const blob = new Blob([JSON.stringify(readCache(), null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `starlight-backup-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function importBackup(file) {
  const text = await file.text();
  const classes = JSON.parse(text);
  if (!Array.isArray(classes)) throw new Error("Invalid backup file");
  for (const c of classes) {
    const body = { id: c.id, name: c.name, stages: c.stages, pupils: c.pupils };
    try {
      await axios.post(`${API}/classes`, body);
    } catch {
      enqueue({ type: "upsertClass", id: c.id, body });
    }
  }
  writeCache(classes);
  return classes;
}
